import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import { API_ENDPOINTS } from '../config/constants';
import '../styles/events.css';

const EventDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [rsvpForm, setRSVPForm] = useState({
    name: '',
    email: '',
    mobile: '',
    status: 'going',
    guests_count: 0
  });
  const [rsvpLoading, setRSVPLoading] = useState(false);
  const [rsvpSuccess, setRSVPSuccess] = useState(false);

  useEffect(() => {
    loadEvent();
  }, []);

  const loadEvent = async () => {
    const eventId = searchParams.get('id');
    
    if (!eventId) {
      // No event ID, redirect back to events page
      navigate('/events');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/events/${eventId}`, {
        headers: {
          [TOKEN_HEADER]: generateToken()
        }
      });
      const data = await response.json();
      
      if (data.success && data.data) {
        setEvent(data.data);
        // Also store in sessionStorage for quick access if user returns
        sessionStorage.setItem('selectedEvent', JSON.stringify(data.data));
      } else {
        setError('Event not found');
        setTimeout(() => navigate('/events'), 2000);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      setError('Failed to load event');
      setTimeout(() => navigate('/events'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit' 
    };
    return date.toLocaleString('en-US', options);
  };

  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return {
      month: date.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
      day: date.getDate(),
      time: date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' })
    };
  };

  const getLocationDisplay = (event) => {
    if (event.location_type === 'virtual') {
      return '💻 Online event';
    } else if (event.location_type === 'physical' && event.location_name) {
      return `📍 ${event.location_name}`;
    }
    return '📍 Location TBA';
  };

  const isPastEvent = () => {
    return new Date(event.start_datetime) <= new Date();
  };

  const handleRSVPSubmit = async (e) => {
    e.preventDefault();
    setRSVPLoading(true);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/events/${event.id}/rsvp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken()
        },
        body: JSON.stringify(rsvpForm)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRSVPSuccess(true);
        setRSVPForm({
          name: '',
          email: '',
          mobile: '',
          status: 'going',
          guests_count: 0
        });
        // Update the event with new count
        setEvent(prev => ({
          ...prev,
          going_count: (prev.going_count || 0) + 1
        }));
        setTimeout(() => {
          setShowRSVPModal(false);
          setRSVPSuccess(false);
        }, 2000);
      } else {
        alert(data.error || 'Failed to submit RSVP. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setRSVPLoading(false);
    }
  };

  const closeModal = () => {
    setShowRSVPModal(false);
    setRSVPSuccess(false);
  };

  if (loading) {
    return (
      <div className="event-details-page">
        <div className="events-loading">
          <div className="spinner"></div>
          <p>Loading event...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-details-page">
        <div className="events-empty">
          <div className="empty-icon">⚠️</div>
          <h2>{error}</h2>
          <p>Redirecting to events page...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  const dateInfo = formatShortDate(event.start_datetime);

  return (
    <div className="event-details-page">
      <div className="event-details-header">
        <button className="back-button" onClick={() => navigate('/events')}>
          ← Back to Events
        </button>
      </div>

      <div className="event-details-container">
        <div className="event-details-main">
          {event.image_url && (
            <div className="event-details-image">
              <img src={event.image_url} alt={event.title} />
              <div className="event-details-title-overlay">{event.title}</div>
              {isPastEvent() && <div className="past-overlay">Past Event</div>}
            </div>
          )}

          <div className="event-details-content">
            <div className="event-details-badges">
              {event.category_name && (
                <span className="event-category-badge">{event.category_name}</span>
              )}
              {event.is_featured && (
                <span className="event-featured-badge">⭐ Featured</span>
              )}
            </div>

            <h1 className="event-details-title">{event.title}</h1>

            <div className="event-details-info">
              <div className="event-info-item">
                <span className="info-icon">📅</span>
                <span className="info-text">{formatDate(event.start_datetime)}</span>
              </div>
              {event.end_datetime && (
                <div className="event-info-item">
                  <span className="info-icon">🏁</span>
                  <span className="info-text">Ends: {formatDate(event.end_datetime)}</span>
                </div>
              )}
              <div className="event-info-item">
                <span className="info-icon">{event.location_type === 'virtual' ? '💻' : '📍'}</span>
                <span className="info-text">
                  {event.location_url ? (
                    <a href={event.location_url} target="_blank" rel="noopener noreferrer" className="location-link">
                      {event.location_type === 'virtual' ? 'Online Event' : event.location_name || 'Location TBA'}
                    </a>
                  ) : (
                    event.location_type === 'virtual' ? 'Online Event' : event.location_name || 'Location TBA'
                  )}
                </span>
              </div>
              {(event.address || event.city) && (
                <div className="event-info-item">
                  <span className="info-icon">🗺️</span>
                  <span className="info-text">
                    {[event.address, event.city, event.state, event.country].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {event.timezone && (
                <div className="event-info-item">
                  <span className="info-icon">🌐</span>
                  <span className="info-text">Timezone: {event.timezone}</span>
                </div>
              )}
              {event.registration_deadline && !isPastEvent() && (
                <div className="event-info-item">
                  <span className="info-icon">⏰</span>
                  <span className="info-text">Register by: {formatDate(event.registration_deadline)}</span>
                </div>
              )}
            </div>

            {event.tags && (
              <div className="event-tags">
                {event.tags.split(',').map((tag, index) => (
                  <span key={index} className="event-tag">{tag.trim()}</span>
                ))}
              </div>
            )}

            {event.description && (
              <div className="event-details-description">
                <h2>About This Event</h2>
                <p>{event.description}</p>
              </div>
            )}

            {event.meeting_link && !isPastEvent() && (
              <div className="event-details-meeting">
                <h2>Join Online</h2>
                <a href={event.meeting_link} target="_blank" rel="noopener noreferrer" className="meeting-link">
                  Click here to join the meeting
                </a>
              </div>
            )}
          </div>
        </div>

        <aside className="event-details-sidebar">
          <div className="event-sidebar-card">
            <div className="event-date-box">
              <span className="date-month">{dateInfo.month}</span>
              <span className="date-day">{dateInfo.day}</span>
              <span className="date-time">{dateInfo.time}</span>
            </div>

            <div className="event-attendees-box">
              <span className="attendees-icon">👥</span>
              <span className="attendees-count">
                {event.going_count || 0} {isPastEvent() ? 'attended' : 'attending'}
              </span>
            </div>

            {event.video_url && (
              <div className="event-video-section">
                <h3>Event Video</h3>
                <div className="video-embed">
                  <iframe
                    src={event.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    title="Event Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            )}

            {!isPastEvent() && (
              <button
                className="rsvp-button-large"
                onClick={() => setShowRSVPModal(true)}
              >
                RSVP Now
              </button>
            )}

            {event.max_attendees && (
              <p className="max-attendees">
                Limited to {event.max_attendees} attendees
              </p>
            )}
          </div>

          {event.organizer && (
            <div className="event-sidebar-card">
              <h3>Organizer</h3>
              <p>{event.organizer}</p>
            </div>
          )}
        </aside>
      </div>

      {/* RSVP Modal */}
      {showRSVPModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="rsvp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            
            {rsvpSuccess ? (
              <div className="rsvp-success">
                <div className="success-icon">✅</div>
                <h2>RSVP Confirmed!</h2>
                <p>Thank you for registering for <strong>{event.title}</strong></p>
                <p className="success-message">
                  We've sent a confirmation email to {rsvpForm.email}
                </p>
              </div>
            ) : (
              <>
                <h2>RSVP for {event.title}</h2>
                <p className="modal-subtitle">
                  {formatDate(event.start_datetime)}
                </p>

                <form onSubmit={handleRSVPSubmit} className="rsvp-form">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={rsvpForm.name}
                      onChange={(e) => setRSVPForm({ ...rsvpForm, name: e.target.value })}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      value={rsvpForm.email}
                      onChange={(e) => setRSVPForm({ ...rsvpForm, email: e.target.value })}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="mobile">Mobile Number (Optional)</label>
                    <input
                      type="tel"
                      id="mobile"
                      value={rsvpForm.mobile}
                      onChange={(e) => setRSVPForm({ ...rsvpForm, mobile: e.target.value })}
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  <div className="form-group">
                    <label>RSVP Status *</label>
                    <div className="status-options">
                      <label className="status-option">
                        <input
                          type="radio"
                          name="status"
                          value="going"
                          checked={rsvpForm.status === 'going'}
                          onChange={(e) => setRSVPForm({ ...rsvpForm, status: e.target.value })}
                        />
                        <span className="status-label">
                          <span className="status-icon">✓</span>
                          Going
                        </span>
                      </label>
                      <label className="status-option">
                        <input
                          type="radio"
                          name="status"
                          value="interested"
                          checked={rsvpForm.status === 'interested'}
                          onChange={(e) => setRSVPForm({ ...rsvpForm, status: e.target.value })}
                        />
                        <span className="status-label">
                          <span className="status-icon">⭐</span>
                          Interested
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="guests">Number of Guests</label>
                    <select
                      id="guests"
                      value={rsvpForm.guests_count}
                      onChange={(e) => setRSVPForm({ ...rsvpForm, guests_count: parseInt(e.target.value) })}
                    >
                      <option value="0">Just me</option>
                      <option value="1">+1 guest</option>
                      <option value="2">+2 guests</option>
                      <option value="3">+3 guests</option>
                      <option value="4">+4 guests</option>
                      <option value="5">+5 guests</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={rsvpLoading}
                  >
                    {rsvpLoading ? 'Submitting...' : 'Confirm RSVP'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
